import React, { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { useUser } from "@/shared/lib/hooks/useUser/useUser";
import { useAdminUI } from "@/shared/config/admin";

/*
 * /:lang/account/products — «Все продукты» (PRODUCTS_DESIGN.md §3), пока
 * только для администраторов. Тот же приём, что у LeagueUsersRoute.
 *
 * До страницы этот адрес ловил общий маршрут «*» (routeConfig, NOT_FOUND) и
 * отвечал голым «404» всем. Так и остаётся для всех, кроме администратора:
 *   не вошёл                                     → «404», и ни одного запроса;
 *   вошёл, не администратор или «как обычный»    → «404»;
 *   администратор                                → каркас кабинета + «Все продукты».
 * Пока грузится список администраторов — тоже «404», а не пустое место: все, кроме
 * администраторов, с первого кадра видят ровно прежнюю страницу. Администратор из
 * списка на сервере (не из пяти основных) при холодном входе видит «404» на миг,
 * пока список не пришёл; обычно он уже в памяти — меню его читает.
 */

const AccountPage = lazy(() => import("@/pages/Account").then((m) => ({ default: m.default })));
const ProductsPage = lazy(() => import("@/pages/ProductsPage").then((m) => ({ default: m.ProductsPage })));

/** Ровно то, что рисует NOT_FOUND в routeConfig. */
const NotFound = () => <div>404</div>;

export const ProductsRoute = () => {
    const { user } = useUser();
    const email = user && typeof user !== "string" ? user.email || "" : "";
    if (!email) return <NotFound />;
    return <AdminOnly email={email} />;
};

const AdminOnly = ({ email }: { email: string }) => {
    const { isAdmin, loading } = useAdminUI(email);
    if (loading || !isAdmin) return <NotFound />;
    // Сама страница — только на /account/products; хвост (…/products/x) — «404», как раньше.
    return (
        <Routes>
            <Route
                element={
                    <Suspense fallback="">
                        <AccountPage />
                    </Suspense>
                }
            >
                <Route
                    index
                    element={
                        <Suspense fallback="">
                            <ProductsPage />
                        </Suspense>
                    }
                />
            </Route>
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
};

export default ProductsRoute;
